import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Spinner from './Spinner';
import { useAuth } from '../AuthContext';
import { TILES } from './Home';

const resolveApiBase = () => {
  const baseEnv = process.env.REACT_APP_API_URL || '';
  const hostname = window.location.hostname;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalHost) return '';
  if (!baseEnv) return '';
  try {
    const envUrl = new URL(baseEnv);
    if (envUrl.origin === window.location.origin) return '';
    return baseEnv.replace(/\/$/, '');
  } catch {
    return baseEnv.replace(/\/$/, '');
  }
};

const normalizePath = (value: string) => {
  const raw = (value || '').trim();
  if (!raw) return '';
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withSlash.length > 1 && withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
};

const toPathCandidates = (value: unknown): string[] => {
  if (typeof value !== 'string') return [];
  const raw = value.trim();
  if (!raw) return [];
  const set = new Set<string>();
  set.add(normalizePath(raw));
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const parsed = new URL(raw);
      set.add(normalizePath(parsed.pathname));
    } catch {
      // ignore invalid URL-like values
    }
  }
  return Array.from(set).filter(Boolean);
};

const UnauthorizedBox: React.FC<{ path: string }> = ({ path }) => (
  <div className="container py-5" style={{ marginTop: 100 }}>
    <div className="alert alert-danger">Você não tem autorização para a página {path}</div>
  </div>
);

const LoadingBox: React.FC = () => (<div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>);

const AuthorizedRoute: React.FC<{ element: React.ReactElement; requiredLink: string }> = ({ element, requiredLink }) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const { userId } = useAuth();

  useEffect(() => {
    const check = async () => {
      const urlBase = resolveApiBase();
      try {
        if (!userId) { setAllowed(false); return; }
        const user = await axios.get(`${urlBase}/api/users/${userId}`, { withCredentials: true });
        if (user.data?.isAdmin === true) { setAllowed(true); return; }
        const rawProfile = user.data?.profile;
        const profileId = String(typeof rawProfile === 'string' ? rawProfile : rawProfile?._id || '');
        if (!profileId) { setAllowed(false); return; }
        const [appsRes, paRes] = await Promise.all([
          axios.get(`${urlBase}/api/applications`, { withCredentials: true }),
          axios.get(`${urlBase}/api/profile-apps`, { withCredentials: true })
        ]);
        const apps: any[] = Array.isArray(appsRes.data) ? appsRes.data : [];
        const links: any[] = Array.isArray(paRes.data) ? paRes.data : [];
        const allowedAppIds = links
          .filter((l) => {
            const linkProfileId = String(typeof l.profile === 'string' ? l.profile : l.profile?._id || '');
            return linkProfileId === profileId;
          })
          .map((l) => String(typeof l.application === 'string' ? l.application : (l.application?._id || '')))
          .filter(Boolean);

        const allowedAppIdSet = new Set(allowedAppIds);
        const requiredCandidates = new Set<string>(toPathCandidates(requiredLink));

        const tileByTitle = new Map<string, string>();
        for (const tile of TILES) tileByTitle.set((tile.title || '').trim().toLowerCase(), tile.link);

        const hasAccess = apps
          .filter((app) => allowedAppIdSet.has(String(app?._id || '')))
          .some((app) => {
            const candidates = new Set<string>();
            toPathCandidates(app?.name).forEach((v) => candidates.add(v));
            toPathCandidates(app?.link).forEach((v) => candidates.add(v));
            toPathCandidates(app?.path).forEach((v) => candidates.add(v));
            toPathCandidates(app?.url).forEach((v) => candidates.add(v));
            toPathCandidates(app?.route).forEach((v) => candidates.add(v));

            const appName = typeof app?.name === 'string' ? app.name.trim().toLowerCase() : '';
            const mappedTileLink = appName ? tileByTitle.get(appName) : null;
            if (mappedTileLink) toPathCandidates(mappedTileLink).forEach((v) => candidates.add(v));

            return Array.from(candidates).some((candidate) => requiredCandidates.has(candidate));
          });

        setAllowed(hasAccess);
      } catch (e) {
        console.error('Erro ao verificar autorização', e);
        setAllowed(false);
      }
    };
    check();
  }, [requiredLink, userId]);

  if (allowed === null) return <LoadingBox />;
  if (!allowed) return <UnauthorizedBox path={requiredLink} />;
  return element;
};

export default AuthorizedRoute;
